/**
 * Session Hooks
 *
 * TanStack Query hooks for session management
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiFetch, buildQueryString } from '../client';
import type {
  ActiveSessionListResponse,
  ActiveSessionFilters,
  SessionHistoryResponse,
  SessionHistoryFilters,
  EndSessionRequest,
} from '../types/session';

/** Query keys for sessions */
export const sessionKeys = {
  all: ['sessions'] as const,
  active: () => [...sessionKeys.all, 'active'] as const,
  activeList: (filters: ActiveSessionFilters) => [...sessionKeys.active(), filters] as const,
  history: () => [...sessionKeys.all, 'history'] as const,
  historyList: (filters: SessionHistoryFilters) => [...sessionKeys.history(), filters] as const,
};

/**
 * Fetch active sessions
 */
export function useActiveSessions(filters: ActiveSessionFilters = {}) {
  return useQuery({
    queryKey: sessionKeys.activeList(filters),
    queryFn: () =>
      apiFetch<ActiveSessionListResponse>(`/api/sessions/active${buildQueryString(filters)}`),
    staleTime: 10_000, // 10 seconds - sessions change frequently
  });
}

/**
 * Fetch session history
 */
export function useSessionHistory(filters: SessionHistoryFilters = {}) {
  return useQuery({
    queryKey: sessionKeys.historyList(filters),
    queryFn: () =>
      apiFetch<SessionHistoryResponse>(`/api/sessions/history${buildQueryString(filters)}`),
    staleTime: 60_000, // 1 minute - history is less volatile
  });
}

/**
 * Manually end a session
 */
export function useEndSession() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ sessionId, ...data }: EndSessionRequest & { sessionId: string }) =>
      apiFetch<void>(`/api/sessions/${sessionId}/end`, {
        method: 'POST',
        body: data,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: sessionKeys.active() });
      queryClient.invalidateQueries({ queryKey: sessionKeys.history() });
    },
  });
}
